import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from '../hooks/useTranslation';

const videos = [
  'https://papirrullen.clientdemo.app/assets/images/factory1.mp4',
  'https://papirrullen.clientdemo.app/assets/images/factory2.mp4',
  'https://papirrullen.clientdemo.app/assets/images/factory3.mp4',
];

const ArrowLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);

const ArrowRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const B2BVideoCarousel: React.FC = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % videos.length);
  }, []);

  const goToPrev = () => {
    setCurrentIndex(prev => (prev - 1 + videos.length) % videos.length);
  };

  useEffect(() => {
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        const videoElement = videoRefs.current[currentIndex];
        if (videoElement) {
          if (entry.isIntersecting) {
            videoElement.play().catch(error => console.error("Video play failed:", error));
          } else {
            videoElement.pause();
          }
        }
      });
    };

    observerRef.current = new IntersectionObserver(handleIntersection, { threshold: 0.5 });
    const currentContainer = containerRef.current;
    if (currentContainer) {
      observerRef.current.observe(currentContainer);
    }

    return () => {
      if (currentContainer) {
        observerRef.current?.unobserve(currentContainer);
      }
    };
  }, [currentIndex]);

  useEffect(() => {
    const videoElement = videoRefs.current[currentIndex];
    
    // Pause all other videos and reset them
    videoRefs.current.forEach((vid, index) => {
        if (vid && index !== currentIndex) {
            vid.pause();
            vid.currentTime = 0;
        }
    });

    if (videoElement) {
      videoElement.addEventListener('ended', goToNext);
    }

    return () => {
      if (videoElement) {
        videoElement.removeEventListener('ended', goToNext);
      }
    };
  }, [currentIndex, goToNext]);

  return (
    <section id="b2b-video" className="py-24 bg-brand-background dark:bg-slate-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-serif text-brand-dark dark:text-white">{t('b2b_video_title')}</h2>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600 dark:text-slate-300">
              {t('b2b_video_subtitle')}
            </p>
        </div>

        <div ref={containerRef} className="relative aspect-video w-full max-w-4xl mx-auto overflow-hidden rounded-xl shadow-2xl group border dark:border-slate-700">
          <div 
            className="flex transition-transform duration-500 ease-in-out h-full" 
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {videos.map((src, index) => (
              <video
                key={src}
// FIX: The ref callback was implicitly returning the assigned element, which is not allowed. Changed the arrow function to use a block body to ensure it returns void, resolving the TypeScript error.
                ref={el => { videoRefs.current[index] = el; }}
                src={src}
                muted
                playsInline
                className="w-full h-full object-cover flex-shrink-0"
              />
            ))}
          </div>

          <button 
            onClick={goToPrev}
            className="absolute top-1/2 left-4 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full transition-opacity opacity-0 group-hover:opacity-100 focus:opacity-100"
            aria-label={t('pagination_prev')}
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <button 
            onClick={goToNext}
            className="absolute top-1/2 right-4 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full transition-opacity opacity-0 group-hover:opacity-100 focus:opacity-100"
            aria-label={t('pagination_next')}
          >
            <ArrowRightIcon className="w-6 h-6" />
          </button>
          <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
            {videos.map((_, index) => (
                <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`block w-3 h-3 rounded-full transition-colors ${
                        index === currentIndex ? 'bg-brand-green' : 'bg-white/50 hover:bg-white/80'
                    }`}
                    aria-label={`Go to video ${index + 1}`}
                ></button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default B2BVideoCarousel;
